import React, { useCallback, useEffect, useRef, useState } from 'react';
import crmClient from './crmClient';
import CompanyDetail from './CompanyDetail';
import { stageLabel, formatAddress, formatCurrency, formatDate, formatDateTime, formatPhone } from './format';
import './VisitRoutesView.css';

const errorMessage = (error, fallback) =>
  (error && error.response && error.response.data && error.response.data.message) || fallback;
const toggleValue = (values, value) => values.includes(value) ? values.filter(item => item !== value) : [...values, value];
const minutes = seconds => `${Math.ceil(seconds / 60)} min`;
const digits = phone => String(phone || '').replace(/[^\d+]/g, '');
const phoneIdentity = phone => {
  const value = String(phone || '').replace(/\D/g, '');
  return value.length === 11 && value.startsWith('1') ? value.slice(1) : value;
};

export const initialRouteForm = {
  title: 'Customer visits',
  cities: '', state: 'LA', start: '', finish: '', stop_count: 20, visit_minutes: 15,
  history: 'ever_ordered', categories: [], stages: ['active', 'warm_inactive', 'cold_inactive'],
  assigned_to_crm_user_id: ''
};

const RouteProgress = ({ progress, title }) => (
  <div className="crm-route-progress">
    <div className="crm-route-heading">
      <strong>{progress.percent_complete}% complete</strong>
      <span>{progress.completed_stops} of {progress.total_stops} stops contacted</span>
    </div>
    <progress aria-label={`${title} progress`} max={100} value={progress.percent_complete} />
    <p className="crm-muted">{progress.remaining_stops} remaining | {progress.visited_stops} visited | {progress.skipped_stops} skipped without contact</p>
  </div>
);

const ContactLogger = ({ stop, outcomes, saving, onSave, onClose }) => {
  const [form, setForm] = useState({ channel: 'visit', outcome: '', contact_name: '', notes: '' });
  const request = useRef(null);
  const update = (key, value) => setForm(previous => ({ ...previous, [key]: value }));
  const submit = event => {
    event.preventDefault();
    const fingerprint = JSON.stringify(form);
    if (!request.current || request.current.fingerprint !== fingerprint) {
      request.current = { fingerprint, id: window.crypto.randomUUID() };
    }
    onSave(stop, { ...form, request_id: request.current.id });
  };
  return (
    <form className="crm-route-log" onSubmit={submit}>
      <h4>Log contact: {stop.company.business_name}</h4>
      <div className="crm-route-fields">
        <label>Contact method
          <select value={form.channel} onChange={event => update('channel', event.target.value)}>
            <option value="visit">In-person visit</option><option value="call">Phone call</option>
            <option value="email">Email</option><option value="other">Other contact</option>
            <option value="skipped">Skipped / could not visit</option>
          </select>
        </label>
        {form.channel !== 'skipped' && <label>Outcome
          <select required value={form.outcome} onChange={event => update('outcome', event.target.value)}>
            <option value="">Select an outcome</option>
            {outcomes.map(outcome => <option key={outcome}>{outcome}</option>)}
          </select>
        </label>}
        <label>Person contacted
          <input maxLength={200} value={form.contact_name} onChange={event => update('contact_name', event.target.value)} />
        </label>
      </div>
      <label>{form.channel === 'skipped' ? 'Reason for skipping (required)' : 'Notes / next steps'}
        <textarea required={form.channel === 'skipped'} maxLength={5000} value={form.notes}
          onChange={event => update('notes', event.target.value)} />
      </label>
      <div className="crm-route-actions">
        <button className="crm-btn" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save to CRM history'}</button>
        <button className="crm-btn crm-btn--ghost" type="button" disabled={saving} onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
};

const VisitRoutesView = ({ meta, initialRouteId }) => {
  const [form, setForm] = useState(initialRouteForm);
  const [routes, setRoutes] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(true);
  const [assigneeError, setAssigneeError] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [listError, setListError] = useState('');
  const [pagination, setPagination] = useState({ offset: 0, limit: 25, total: 0 });
  const [route, setRoute] = useState(null);
  const [title, setTitle] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loggingStop, setLoggingStop] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [showForm, setShowForm] = useState(!initialRouteId);
  const loadSequence = useRef(0);
  const listSequence = useRef(0);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; loadSequence.current++; listSequence.current++; }, []);

  const loadList = useCallback(async (offset = 0, assignedTo = assignmentFilter) => {
    const sequence = ++listSequence.current;
    setLoadingList(true);
    setListError('');
    try {
      const payload = await crmClient.listVisitRoutes({ offset, ...(assignedTo ? { assigned_to: assignedTo } : {}) });
      if (!payload || !Array.isArray(payload.routes) || payload.routes.some(item => !item.progress)) throw new Error('Missing route progress');
      if (mounted.current && sequence === listSequence.current) {
        setRoutes(payload.routes);
        setPagination(payload.pagination);
      }
    } catch (failure) {
      if (mounted.current && sequence === listSequence.current) setListError(errorMessage(failure, 'Unable to load saved visit routes and progress. Refresh the list to retry.'));
    } finally {
      if (mounted.current && sequence === listSequence.current) setLoadingList(false);
    }
  }, [assignmentFilter]);

  const loadAssignees = useCallback(async () => {
    setLoadingAssignees(true);
    setAssigneeError('');
    try {
      const payload = await crmClient.visitRouteAssignees();
      if (!Array.isArray(payload)) throw new Error('Missing CRM users');
      if (mounted.current) setAssignees(payload);
    } catch (failure) {
      if (mounted.current) setAssigneeError(errorMessage(failure, 'Unable to load route assignees. Refresh the list to retry.'));
    } finally {
      if (mounted.current) setLoadingAssignees(false);
    }
  }, []);

  const showRoute = useCallback(payload => {
    if (!payload || !payload.plan || !Array.isArray(payload.plan.stops) || !payload.progress) throw new Error('Missing route plan or progress');
    setRoute(payload);
    setTitle(payload.title);
    setAssigneeId(payload.assigned_to_crm_user_id || '');
    setShowForm(false);
    const url = new URL(window.location.href);
    url.searchParams.set('visit_route', payload.crm_visit_route_id);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }, []);

  const loadRoute = useCallback(async id => {
    const sequence = ++loadSequence.current;
    setBusy(true);
    setError('');
    setLoggingStop(null);
    setCompanyId(null);
    try {
      const payload = await crmClient.getVisitRoute(id);
      if (mounted.current && sequence === loadSequence.current) showRoute(payload);
    } catch (failure) {
      if (mounted.current && sequence === loadSequence.current) setError(errorMessage(failure, 'Unable to load the route. Refresh and try again.'));
    } finally {
      if (mounted.current && sequence === loadSequence.current) setBusy(false);
    }
  }, [showRoute]);

  useEffect(() => {
    loadList();
    loadAssignees();
    if (initialRouteId) loadRoute(initialRouteId);
  // The initial link is consumed once; opening saved routes is handled locally.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => loadList(pagination.offset), 60000);
    return () => window.clearInterval(timer);
  }, [loadList, pagination.offset]);

  const update = (key, value) => setForm(previous => ({ ...previous, [key]: value }));
  const generate = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    setLoggingStop(null);
    setCompanyId(null);
    try {
      const payload = await crmClient.generateVisitRoute({
        ...form, cities: form.cities.split(',').map(city => city.trim()).filter(Boolean),
        stop_count: Number(form.stop_count), visit_minutes: Number(form.visit_minutes),
        assigned_to_crm_user_id: form.assigned_to_crm_user_id || null,
      });
      if (mounted.current) {
        showRoute(payload);
        setNotice('Route saved and visible to everyone with CRM access.');
        setAssignmentFilter('');
        loadList(0, '');
      }
    } catch (failure) {
      if (mounted.current) setError(errorMessage(failure, 'Route generation failed. No confirmed saved route was returned; refresh saved routes before retrying.'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const editRoute = async changes => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const payload = await crmClient.editVisitRoute(route.crm_visit_route_id, { version: route.version, ...changes });
      if (mounted.current) {
        showRoute(payload);
        setNotice('Route updated.');
        loadList(pagination.offset);
      }
    } catch (failure) {
      if (mounted.current) setError(errorMessage(failure, 'Unable to edit this route. Refresh to see the latest saved version.'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const moveStop = (index, direction) => {
    const ids = route.plan.stops.map(stop => stop.stop_id);
    [ids[index], ids[index + direction]] = [ids[index + direction], ids[index]];
    editRoute({ stop_ids: ids });
  };
  const removeStop = stop => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`Remove ${stop.company.business_name} and recalculate driving directions?`)) {
      editRoute({ stop_ids: route.plan.stops.filter(item => item.stop_id !== stop.stop_id).map(item => item.stop_id) });
    }
  };

  const saveContact = async (stop, body) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await crmClient.logRouteContact(route.crm_visit_route_id, stop.stop_id, body);
    } catch (failure) {
      if (mounted.current) {
        setError(errorMessage(failure, 'Could not confirm contact was saved. Retry unchanged to avoid a duplicate.'));
        setBusy(false);
      }
      return;
    }
    if (!mounted.current) return;
    setLoggingStop(null);
    setNotice('Contact saved to the route and linked account history.');
    await Promise.all([loadRoute(route.crm_visit_route_id), loadList(pagination.offset)]);
  };

  const plan = route && route.plan;
  const categoryKeys = Object.keys((meta && meta.categories) || {}).filter(category => category !== 'Individual');
  const outcomes = (meta && meta.call_outcomes) || [];
  const activities = route ? route.activities || [] : [];
  const uniqueLogs = activities.filter((activity, index, all) =>
    all.findIndex(item => item.metadata.request_id === activity.metadata.request_id) === index);
  const visited = plan ? plan.stops.filter(stop => uniqueLogs.some(activity =>
    activity.metadata.stop_id === stop.stop_id && activity.metadata.channel === 'visit')).length : 0;
  const shareUrl = route ? `${window.location.origin}${window.location.pathname}?visit_route=${encodeURIComponent(route.crm_visit_route_id)}` : '';
  const assigneeLabel = item => item.assignee
    ? `${item.assignee.email || item.assignee.crm_user_id}${item.assignee.available ? '' : ' (access removed)'}`
    : 'Unassigned';

  return (
    <div className={`crm-visit-routes${companyId ? ' crm-companies--split' : ''}`}>
      <div className="crm-companies-main">
        <div className="crm-route-heading">
          <div><h2>Visit Routes</h2><p className="crm-muted">Plan a day of customer visits, prepare from CRM history, and log contact at each stop.</p></div>
          <button type="button" className="crm-btn" disabled={busy} onClick={() => setShowForm(open => !open)}>
            {showForm ? 'Hide planner' : route ? 'Create another route' : 'Plan a new route'}
          </button>
        </div>
        {error && <div role="alert" className="crm-inline-error">{error}</div>}
        {notice && <p role="status" className="crm-route-notice">{notice}</p>}
        {assigneeError && <div role="alert" className="crm-inline-error">{assigneeError}</div>}
        {showForm && <form className="crm-route-panel" onSubmit={generate}>
          <h3>Where do you want to visit?</h3>
          <p className="crm-muted">Each generation saves a separate route. Existing routes and their contact history stay available below.</p>
          <div className="crm-route-fields">
            <label>Route name<input required maxLength={160} value={form.title} onChange={event => update('title', event.target.value)} /></label>
            <label>Assign new route to
              <select disabled={loadingAssignees || Boolean(assigneeError)} value={form.assigned_to_crm_user_id}
                onChange={event => update('assigned_to_crm_user_id', event.target.value)}>
                <option value="">Unassigned</option>
                {assignees.map(person => <option key={person.crm_user_id} value={person.crm_user_id}>{person.email || person.crm_user_id}</option>)}
              </select>
            </label>
            <label>Cities / areas (comma-separated)
              <input required placeholder="Lake Charles, Sulphur" value={form.cities} onChange={event => update('cities', event.target.value)} />
              <small>Exact CRM city names, including Moss Bluff separately if needed.</small>
            </label>
            <label>State<input maxLength={50} value={form.state} onChange={event => update('state', event.target.value)} /></label>
            <label>Starting location<input required maxLength={300} placeholder="Full address or city" value={form.start} onChange={event => update('start', event.target.value)} /></label>
            <label>Finishing location<input required maxLength={300} placeholder="Full address; use start address for a round trip" value={form.finish} onChange={event => update('finish', event.target.value)} /></label>
            <label>Number of businesses<input required type="number" min="1" max="25" value={form.stop_count} onChange={event => update('stop_count', event.target.value)} /></label>
            <label>Minutes per visit<input required type="number" min="5" max="120" value={form.visit_minutes} onChange={event => update('visit_minutes', event.target.value)} /></label>
            <label>Customer history<select value={form.history} onChange={event => update('history', event.target.value)}>
              <option value="ever_ordered">Current or past customers (has orders)</option>
              <option value="never_ordered">New leads (no recorded orders)</option>
              <option value="all">Customers and new leads</option>
            </select></label>
          </div>
          <fieldset><legend>Industries</legend><p className="crm-muted">Leave unchecked to include all industries, including unclassified businesses.</p>
            <div className="crm-route-checks">{categoryKeys.map(category => <label key={category}>
              <input type="checkbox" checked={form.categories.includes(category)}
                onChange={() => update('categories', toggleValue(form.categories, category))} />
              {category === 'Pharmaceutical' ? 'Medical / pharmaceutical' : category}
            </label>)}</div>
          </fieldset>
          <fieldset><legend>Customer stages</legend><div className="crm-route-checks">
            {((meta && meta.stages) || []).filter(stage => stage !== 'lost').map(stage => <label key={stage}>
              <input type="checkbox" checked={form.stages.includes(stage)} onChange={() => update('stages', toggleValue(form.stages, stage))} />
              {stageLabel(stage)}
            </label>)}
          </div></fieldset>
          <p className="crm-muted">Business accounts only. Lost accounts and obvious test/non-street addresses are excluded. Planning prioritizes a compact route among high-order-count matching accounts. It does not verify opening hours.</p>
          <button className="crm-btn" type="submit" disabled={busy || !form.stages.length}>{busy ? 'Working...' : 'Generate and save shared route'}</button>
        </form>}

        <section className="crm-route-panel" aria-label="Route overview">
          <div className="crm-route-heading"><h3>Shared saved routes</h3>
            <button type="button" className="crm-btn crm-btn--ghost" disabled={loadingList || busy}
              onClick={() => { loadList(pagination.offset); loadAssignees(); }}>Refresh list</button>
          </div>
          <p className="crm-muted">Compare saved routes together. Progress counts distinct stops with any logged contact; skips alone do not count. Refreshes every minute.</p>
          <label className="crm-route-filter">Show routes
            <select value={assignmentFilter} disabled={busy} onChange={event => {
              setAssignmentFilter(event.target.value);
              loadList(0, event.target.value);
            }}>
              <option value="">Everyone's routes</option>
              <option value="me">Assigned to me</option>
              <option value="unassigned">Unassigned routes</option>
              {assignees.map(person => <option key={person.crm_user_id} value={person.crm_user_id}>{person.email || person.crm_user_id}</option>)}
            </select>
          </label>
          {listError && <div role="alert" className="crm-inline-error">{listError}</div>}
          {loadingList && <p role="status">Loading saved routes...</p>}
          {!loadingList && !listError && routes.length === 0 && <p>{assignmentFilter ? 'No routes match this assignment.' : 'No routes saved yet.'}</p>}
          <ul className="crm-route-saved">{routes.map(item => <li key={item.crm_visit_route_id}>
            <article className={`crm-route-overview-card${route && route.crm_visit_route_id === item.crm_visit_route_id ? ' crm-route-overview-card--selected' : ''}`}
              aria-label={`${item.title} route overview`}>
              <button type="button" className="crm-btn crm-btn--ghost" disabled={busy} onClick={() => loadRoute(item.crm_visit_route_id)}>{item.title}</button>
              <p><strong>Assigned to:</strong> {assigneeLabel(item)}</p>
              <RouteProgress progress={item.progress} title={item.title} />
              <p className="crm-muted">{item.filters.cities.join(', ')} | {item.creator_email || 'CRM teammate'} | {formatDateTime(item.created_at)}</p>
            </article>
          </li>)}</ul>
          {pagination.total > pagination.limit && <div className="crm-route-actions">
            <button type="button" className="crm-btn crm-btn--ghost" disabled={loadingList || busy || pagination.offset === 0}
              onClick={() => loadList(Math.max(0, pagination.offset - pagination.limit))}>Previous</button>
            <span>{pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}</span>
            <button type="button" className="crm-btn crm-btn--ghost" disabled={loadingList || busy || pagination.offset + pagination.limit >= pagination.total}
              onClick={() => loadList(pagination.offset + pagination.limit)}>Next</button>
          </div>}
        </section>

        {route && <section aria-label="Saved visit route">
          <div className="crm-route-panel">
            <div className="crm-route-heading"><h3>{route.title}</h3>
              <button type="button" className="crm-btn crm-btn--ghost" disabled={busy} onClick={() => loadRoute(route.crm_visit_route_id)}>Refresh route</button>
            </div>
            <p>{plan.stops.length} businesses | {(plan.driving_meters / 1609.344).toFixed(1)} miles | {minutes(plan.driving_seconds)} driving, including finish | {minutes(plan.total_seconds)} with visits</p>
            <RouteProgress progress={route.progress} title={route.title} />
            <p><strong>Assigned to:</strong> {assigneeLabel(route)}</p>
            <p>{visited} of {plan.stops.length} stops have an in-person visit logged. Other contact and skips remain in the history below.</p>
            <p><strong>Start:</strong> {route.filters.start} <strong>Finish:</strong> {route.filters.finish}</p>
            <p className="crm-muted">{plan.method} Add time for lunch and parking. Homework snapshot: {formatDateTime(plan.homework_as_of)}.</p>
            <label>Share with a CRM teammate<input className="crm-route-share" readOnly value={shareUrl} onFocus={event => event.target.select()} /></label>
            {route.can_edit && <form className="crm-route-actions" onSubmit={event => { event.preventDefault(); editRoute({ title }); }}>
              <label>Rename route<input required maxLength={160} value={title} onChange={event => setTitle(event.target.value)} /></label>
              <button type="submit" className="crm-btn crm-btn--ghost" disabled={busy || title === route.title}>Save name</button>
            </form>}
            {route.can_edit && <form className="crm-route-actions" onSubmit={event => {
              event.preventDefault();
              editRoute({ assigned_to_crm_user_id: assigneeId || null });
            }}>
              <label>Assign this route to
                <select value={assigneeId} disabled={loadingAssignees || Boolean(assigneeError)}
                  onChange={event => setAssigneeId(event.target.value)}>
                  <option value="">Unassigned</option>
                  {route.assigned_to_crm_user_id && !assignees.some(person => person.crm_user_id === route.assigned_to_crm_user_id) &&
                    <option disabled value={route.assigned_to_crm_user_id}>{assigneeLabel(route)}</option>}
                  {assignees.map(person => <option key={person.crm_user_id} value={person.crm_user_id}>{person.email || person.crm_user_id}</option>)}
                </select>
              </label>
              <button type="submit" className="crm-btn crm-btn--ghost"
                disabled={busy || loadingAssignees || Boolean(assigneeError) || assigneeId === (route.assigned_to_crm_user_id || '')}>Save assignment</button>
            </form>}
            {!route.can_edit && <p className="crm-muted">You can log contact. Only the creator or an admin can change this plan.</p>}
            {plan.warnings.length > 0 && <details><summary>Planning warnings ({plan.warnings.length})</summary>
              <ul>{plan.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
            </details>}
            <h4>GPS navigation</h4>
            <p className="crm-muted">Open sections in order. Each link has at most three intermediate waypoints. The last section includes your finishing location.</p>
            <div className="crm-route-actions">{route.navigation.map(section => <a className="crm-btn" key={section.number}
              href={section.url} target="_blank" rel="noopener noreferrer">GPS section {section.number}</a>)}</div>
          </div>
          {plan.stops.map((stop, index) => {
            const history = uniqueLogs.filter(activity => activity.metadata.stop_id === stop.stop_id);
            const phoneRestricted = stop.homework.contacts.some(contact => contact.do_not_call && phoneIdentity(contact.phone) === phoneIdentity(stop.company.main_phone));
            const locationUrl = `https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=${encodeURIComponent(`${stop.location.lat},${stop.location.lng}`)}`;
            return <article className="crm-route-panel" key={stop.stop_id}>
              <div className="crm-route-heading">
                <h3>{index + 1}. {stop.company.business_name}</h3>
                <span>{history.length ? `Latest: ${history[0].metadata.outcome}` : 'Not contacted on this route'}</span>
              </div>
              <p>{formatAddress(stop.company)}</p>
              <p>{stageLabel(stop.company.crm_stage)} | {stop.company.customer_category || 'Unclassified'} | {stop.company.lifetime_orders} orders | {formatCurrency(stop.company.lifetime_revenue)} lifetime | Last order: {formatDate(stop.company.last_order_date)}</p>
              <p className="crm-muted">{minutes(stop.drive_seconds)} drive from previous point; allow {plan.visit_minutes} minutes for visit.</p>
              {stop.accounts.length > 1 && <p>Combined accounts (one location; logs go to every linked account): {stop.accounts.map(account => account.business_name).join(', ')}. Displayed metrics are for the primary account, not summed.</p>}
              <div className="crm-route-actions">
                <a className="crm-btn crm-btn--ghost" href={locationUrl} target="_blank" rel="noopener noreferrer">Navigate here</a>
                {stop.company.main_phone && !phoneRestricted && <a className="crm-btn crm-btn--ghost" href={`tel:${digits(stop.company.main_phone)}`}>Call {formatPhone(stop.company.main_phone)}</a>}
                <button type="button" className="crm-btn" disabled={busy} onClick={() => { setLoggingStop(stop.stop_id); setError(''); }}>Log visit / contact</button>
                <button type="button" className="crm-btn crm-btn--ghost" onClick={() => setCompanyId(stop.company.crm_company_id)}>Open account</button>
                {route.can_edit && <>
                  <button type="button" className="crm-btn crm-btn--ghost" aria-label={`Move ${stop.company.business_name} earlier`}
                    disabled={busy || index === 0} onClick={() => moveStop(index, -1)}>Move up</button>
                  <button type="button" className="crm-btn crm-btn--ghost" aria-label={`Move ${stop.company.business_name} later`}
                    disabled={busy || index === plan.stops.length - 1} onClick={() => moveStop(index, 1)}>Move down</button>
                  <button type="button" className="crm-btn crm-btn--ghost" disabled={busy || plan.stops.length === 1 || history.length > 0} onClick={() => removeStop(stop)}>Remove</button>
                </>}
              </div>
              <details><summary>Pre-visit homework and notes</summary>
                <p><strong>Suggested conversation:</strong> {stop.homework.talking_point}</p>
                <p className="crm-muted">{stop.homework.caveat}</p>
                <h4>Account notes</h4><p className="crm-route-notes">{stop.homework.account_notes || 'No account notes recorded.'}</p>
                <h4>Current contacts and communication preferences</h4>
                {stop.homework.contacts.length === 0 ? <p>No linked contacts recorded. Use Open account to add one.</p> :
                  <ul>{stop.homework.contacts.map(contact => <li key={contact.crm_contact_id}>
                    <strong>{contact.name || 'Unnamed contact'}</strong> {contact.title || ''} {contact.email || ''}
                    {contact.phone && !contact.do_not_call && <> | <a href={`tel:${digits(contact.phone)}`}>{formatPhone(contact.phone)}</a></>}
                    {(contact.do_not_call || contact.do_not_email || contact.do_not_text) &&
                      <p className="crm-route-caution">Contact restrictions: {[contact.do_not_call && 'Do not call', contact.do_not_email && 'Do not email', contact.do_not_text && 'Do not text'].filter(Boolean).join(', ')}</p>}
                    {contact.notes && <p className="crm-route-notes">{contact.notes}</p>}
                  </li>)}</ul>}
                <h4>Recent CRM history at generation</h4>
                {stop.homework.recent_activities.length === 0 ? <p>No prior activities recorded.</p> :
                  <ul>{stop.homework.recent_activities.map((activity, activityIndex) => <li key={activityIndex}>
                    {formatDateTime(activity.occurred_at)} | {activity.activity_type}<p className="crm-route-notes">{activity.description}</p>
                  </li>)}</ul>}
              </details>
              {loggingStop === stop.stop_id && <ContactLogger key={stop.stop_id} stop={stop} outcomes={outcomes} saving={busy}
                onSave={saveContact} onClose={() => setLoggingStop(null)} />}
              {history.length > 0 && <details open><summary>Contact logged on this route</summary><ul>
                {history.map(activity => <li key={activity.crm_activity_id}>
                  <strong>{activity.metadata.channel}: {activity.metadata.outcome}</strong> | {formatDateTime(activity.occurred_at)} | {activity.metadata.actor_email || activity.actor}
                  {activity.metadata.contact_name && <p>Contact: {activity.metadata.contact_name}</p>}
                  <p className="crm-route-notes">{activity.metadata.notes}</p>
                </li>)}
              </ul></details>}
            </article>;
          })}
        </section>}
      </div>
      {companyId && <CompanyDetail key={companyId} companyId={companyId} meta={meta} onClose={() => setCompanyId(null)} />}
    </div>
  );
};

export default VisitRoutesView;
